export default function (Alpine) {

    const _openPopovers = new Set();

    document.addEventListener('pointerdown', (e) => {
        _openPopovers.forEach(instance => {
            const inRoot   = instance.$root.contains(e.target);
            const inTarget = instance._targetEl?.contains(e.target);
            if (!inRoot && !inTarget) {
                if (instance._clickedInside) instance.close();
            } else {
                instance._clickedInside = true;
            }
        });
    });

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            _openPopovers.forEach(instance => instance.close());
        }
    });

    // ── Tooltip ──────────────────────────────────────────────────
    function _initTooltip(el) {
        if (el._tooltipInit) return;
        el._tooltipInit = true;

        const text = el.getAttribute('tooltip');
        if (!text) return;

        const tip = document.createElement('div');
        tip.setAttribute('role', 'tooltip');
        tip.setAttribute('data-tooltip', '');
        tip.textContent = text;
        Object.assign(tip.style, {
            position:      'fixed',
            zIndex:        '9999',
            visibility:    'hidden',
            pointerEvents: 'none',
        });
        document.body.appendChild(tip);

        const placement  = el.getAttribute('placement')   ?? 'top';
        const offset     = parseInt(el.getAttribute('offset')       ?? '6',  10);
        const openDelay  = parseInt(el.getAttribute('open-delay')   ?? '0',  10);
        const closeDelay = parseInt(el.getAttribute('close-delay')  ?? '0',  10);

        const tipId = `tt-${Math.random().toString(36).slice(2,7)}`;
        tip.id = tipId;
        el.setAttribute('aria-describedby', tipId);

        let openTimer = null, closeTimer = null;

        const position = () => {
            const tr   = el.getBoundingClientRect();
            const rect = tip.getBoundingClientRect();
            const mW   = rect.width  || tip.scrollWidth  || 100;
            const mH   = rect.height || tip.scrollHeight || 30;
            const vW  = window.innerWidth;
            const vH  = window.innerHeight;
            const gap = offset;
            const [primary, align] = placement.split('-');
            let resolved, top, left;

            switch (primary) {
                case 'bottom': resolved='bottom'; top=tr.bottom+gap; left=tr.left; break;
                case 'left':   resolved='left';   top=tr.top;        left=tr.left-mW-gap; break;
                case 'right':  resolved='right';  top=tr.top;        left=tr.right+gap; break;
                default:
                    resolved = tr.top - mH - gap > 0 ? 'top' : 'bottom';
                    top  = resolved === 'top' ? tr.top-mH-gap : tr.bottom+gap;
                    left = tr.left;
            }

            if (resolved === 'top' || resolved === 'bottom') {
                left = tr.left + tr.width/2 - mW/2;
                if (align === 'end') left = tr.right - mW;
                if (left + mW > vW - gap) left = vW - mW - gap;
                if (left < gap)           left = gap;
            } else {
                top = tr.top + tr.height/2 - mH/2;
                if (top + mH > vH - gap) top = vH - mH - gap;
                if (top < gap)           top = gap;
            }

            Object.assign(tip.style, { top: `${top}px`, left: `${left}px` });
            tip.setAttribute('data-placement', resolved);
        };

        const getDuration = () => {
            const s = getComputedStyle(tip);
            const t = parseFloat(s.transitionDuration) || 0;
            const a = parseFloat(s.animationDuration)  || 0;
            return Math.max(t, a) * 1000;
        };

        let _measured = false;

        const show = () => {
            clearTimeout(closeTimer);
            tip.removeAttribute('closing');
            openTimer = setTimeout(() => {
                tip.toggleAttribute('open', true);
                tip.setAttribute('opening', '');

                const doPosition = () => {
                    position();
                    tip.style.visibility = '';
                    tip.style.pointerEvents = '';
                    const dur = getDuration();
                    if (dur > 0) setTimeout(() => tip.removeAttribute('opening'), dur);
                    else tip.removeAttribute('opening');
                };

                if (!_measured) {
                    // İlk açılışta CSS henüz uygulanmamış olabilir
                    // 2 frame bekle, layout kesinleşsin
                    requestAnimationFrame(() => requestAnimationFrame(() => {
                        _measured = true;
                        doPosition();
                    }));
                } else {
                    doPosition();
                }
            }, openDelay);
        };

        const hide = () => {
            clearTimeout(openTimer);
            closeTimer = setTimeout(() => {
                const dur = getDuration();
                if (dur > 0) {
                    tip.setAttribute('closing', '');
                    setTimeout(() => {
                        tip.removeAttribute('open');
                        tip.removeAttribute('closing');
                        tip.removeAttribute('data-placement');
                        tip.style.visibility = 'hidden';
                        tip.style.pointerEvents = 'none';
                    }, dur);
                } else {
                    tip.removeAttribute('open');
                    tip.removeAttribute('data-placement');
                    tip.style.visibility = 'hidden';
                    tip.style.pointerEvents = 'none';
                }
            }, closeDelay);
        };

        el.addEventListener('mouseenter', show);
        el.addEventListener('mouseleave', hide);
        el.addEventListener('focus',      show);
        el.addEventListener('blur',       hide);

        el._tooltipDestroy = () => {
            clearTimeout(openTimer);
            clearTimeout(closeTimer);
            tip.remove();
        };
    }

    // Sayfa yüklenince tüm [tooltip] elementlerini init et
    document.addEventListener('DOMContentLoaded', () => {
        document.querySelectorAll('[tooltip]').forEach(_initTooltip);
    });

    // Alpine component'leri sonradan eklenirse de çalışsın
    Alpine.directive('tooltip', (el) => {
        _initTooltip(el);
        return () => el._tooltipDestroy?.();
    });

    Alpine.data('popover', () => ({

        open:           false,
        _targetEl:      null,
        _mode:          'click',
        _placement:     'auto',
        _offset:        8,
        _openDelay:     0,
        _closeDelay:    150,
        _focusTrap:     false,
        _arrowEl:       null,
        _cleanup:       [],
        _closeTimer:    null,
        _openTimer:     null,
        _reposition:    null,
        _hoverTracker:  null,
        _clickedInside: false,

        init() {
            const root = this.$root;
            const self = this;

            self._mode      = root.getAttribute('trigger')    ?? 'click';
            self._placement = root.getAttribute('placement')  ?? 'auto';
            self._offset    = parseInt(root.getAttribute('offset')       ?? '8',   10);
            self._openDelay = parseInt(root.getAttribute('open-delay')   ?? '0',   10);
            self._closeDelay= parseInt(root.getAttribute('close-delay')  ?? '150', 10);
            self._focusTrap = root.hasAttribute('focus-trap');

            const defaultOpen = root.hasAttribute('open');

            const sel = root.getAttribute('target');
            if (sel) {
                self._targetEl = document.querySelector(sel);
                if (!self._targetEl) console.warn(`[popover] target bulunamadı: ${sel}`);
            } else {
                self._targetEl = root.previousElementSibling;
            }

            self._arrowEl = root.querySelector('[popover-arrow]');

            document.body.appendChild(root);
            Object.assign(root.style, { position: 'fixed', zIndex: '9999' });

            root.setAttribute('role', 'dialog');
            root.setAttribute('aria-modal', self._focusTrap ? 'true' : 'false');

            self._initDismiss();
            self._bindTarget();
            self._watch();

            if (defaultOpen) self.$nextTick(() => self._open());
        },

        destroy() {
            this._cleanup.forEach(({ el, type, fn }) => el.removeEventListener(type, fn));
            this._cleanup = [];
            this._stopHoverTracker();
            if (this._reposition) {
                window.removeEventListener('resize', this._reposition);
                window.removeEventListener('scroll', this._reposition, true);
            }
            _openPopovers.delete(this);
        },


        _initDismiss() {
            const self = this;
            self.$root.querySelectorAll('[popover-dismiss]').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    self.close();
                });
            });
        },


        _bindTarget() {
            const self = this;
            const el   = self._targetEl;
            if (!el) return;

            const isTouchDevice = () => window.matchMedia('(hover: none)').matches;

            if (self._mode === 'hover') {
                self._hoverTracker = (e) => {
                    if (!self.open) return;
                    const hit  = document.elementFromPoint(e.clientX, e.clientY);
                    const over = el.contains(hit) || self.$root.contains(hit);
                    if (over) self._cancelClose(); else self._scheduleClose();
                };
                self._on(el, 'mouseenter', () => {
                    if (isTouchDevice()) return;
                    self._cancelClose();
                    self._scheduleOpen();
                    self._startHoverTracker();
                });
                self._on(el, 'mouseleave', () => {
                    if (isTouchDevice()) return;
                    if (!self.open) self._cancelOpen();
                });
            }

            self._on(el, 'click', (e) => {
                if (el.tagName === 'A') e.preventDefault();
                if (self._mode === 'hover' && !isTouchDevice()) return;
                e.stopPropagation();
                self.toggle();
            });

            self._on(el, 'keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); self.toggle(); }
                if (e.key === 'Escape') self.close();
            });

            if (self._focusTrap) {
                self._on(self.$root, 'keydown', (e) => {
                    if (e.key !== 'Tab') return;
                    const focusable = [...self.$root.querySelectorAll(
                        'a,button,input,textarea,select,[tabindex]:not([tabindex="-1"])'
                    )].filter(el => !el.disabled);
                    if (!focusable.length) { e.preventDefault(); return; }
                    const first = focusable[0];
                    const last  = focusable[focusable.length - 1];
                    if (e.shiftKey) {
                        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
                    } else {
                        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
                    }
                });
            }
        },


        toggle() { this.open ? this.close() : this._open(); },

        _getDuration(el) {
            if (!el) return 0;
            const s = getComputedStyle(el);
            const t = parseFloat(s.transitionDuration) || 0;
            const a = parseFloat(s.animationDuration)  || 0;
            return Math.max(t, a) * 1000;
        },

        _open() {
            const self = this;
            self._cancelClose();
            self._cancelOpen();
            self.$root.removeAttribute('closing');
            _openPopovers.forEach(d => { if (d !== self) d.close(); });
            self.open = true;
            self._clickedInside = false;
            _openPopovers.add(self);
            self.$nextTick(() => {
                self.$root.setAttribute('opening', '');
                requestAnimationFrame(() => {
                    self._position();
                    const dur = self._getDuration(self.$root);
                    if (dur > 0) setTimeout(() => self.$root.removeAttribute('opening'), dur);
                    else self.$root.removeAttribute('opening');
                });
                if (self._focusTrap) {
                    const first = self.$root.querySelector(
                        'a,button,input,textarea,select,[tabindex]:not([tabindex="-1"])'
                    );
                    first?.focus();
                }
            });
        },

        close() {
            if (!this.open) return;
            this._cancelClose();
            this._cancelOpen();
            this._stopHoverTracker();
            const dur = this._getDuration(this.$root);
            if (dur > 0) {
                this.$root.setAttribute('closing', '');
                setTimeout(() => {
                    this.open = false;
                    _openPopovers.delete(this);
                    this.$root.removeAttribute('closing');
                    this.$root.removeAttribute('data-placement');
                    if (this._arrowEl) this._arrowEl.removeAttribute('style');
                }, dur);
            } else {
                this.open = false;
                _openPopovers.delete(this);
                this.$root.removeAttribute('data-placement');
                if (this._arrowEl) this._arrowEl.removeAttribute('style');
            }
        },


        _scheduleOpen() {
            const self = this;
            if (self._openDelay <= 0) { self._open(); return; }
            self._openTimer = setTimeout(() => { self._openTimer = null; self._open(); }, self._openDelay);
        },
        _cancelOpen() { if (this._openTimer) { clearTimeout(this._openTimer); this._openTimer = null; } },

        _scheduleClose() {
            if (this._closeTimer) return;
            this._closeTimer = setTimeout(() => { this._closeTimer = null; this.close(); }, this._closeDelay);
        },
        _cancelClose() { if (this._closeTimer) { clearTimeout(this._closeTimer); this._closeTimer = null; } },

        _startHoverTracker() {
            if (this._hoverTracker) document.addEventListener('mousemove', this._hoverTracker, { passive: true });
        },
        _stopHoverTracker() {
            if (this._hoverTracker) document.removeEventListener('mousemove', this._hoverTracker);
        },


        _position() {
            const self    = this;
            const menu    = self.$root;
            const trigger = self._targetEl;
            if (!trigger) return;

            const gap = self._offset;
            const tr  = trigger.getBoundingClientRect();
            const mW  = menu.offsetWidth  || 200;
            const mH  = menu.offsetHeight || 100;
            const vW  = window.innerWidth;
            const vH  = window.innerHeight;

            let top, left, resolved;
            const [primary, align] = self._placement.split('-');

            switch (primary) {
                case 'top':    resolved='top';    top=tr.top-mH-gap;    left=tr.left; break;
                case 'bottom': resolved='bottom'; top=tr.bottom+gap;    left=tr.left; break;
                case 'left':   resolved='left';   top=tr.top;           left=tr.left-mW-gap; break;
                case 'right':  resolved='right';  top=tr.top;           left=tr.right+gap; break;
                default:
                    resolved = (vH-tr.bottom-gap >= mH || vH-tr.bottom >= tr.top) ? 'bottom' : 'top';
                    top  = resolved === 'bottom' ? tr.bottom+gap : tr.top-mH-gap;
                    left = tr.left;
            }

            if (resolved === 'top' || resolved === 'bottom') {
                if (align === 'center') left = tr.left + tr.width/2 - mW/2;
                else if (align === 'end') left = tr.right - mW;
                if (left + mW > vW - gap) left = vW - mW - gap;
                if (left < gap)           left = gap;
            } else {
                if (align === 'center') top = tr.top + tr.height/2 - mH/2;
                else if (align === 'end') top = tr.bottom - mH;
                if (top + mH > vH - gap) top = vH - mH - gap;
                if (top < gap)           top = gap;
            }

            menu.style.top  = `${top}px`;
            menu.style.left = `${left}px`;
            menu.setAttribute('data-placement', resolved);

            if (self._arrowEl) self._positionArrow(resolved, tr, top, left, mW, mH);
        },

        _positionArrow(placement, tr, menuTop, menuLeft, mW, mH) {
            const self    = this;
            const arrow   = self._arrowEl;
            const aW      = arrow.offsetWidth  || 12;
            const aH      = arrow.offsetHeight || 12;

            let arrowTop, arrowLeft;

            if (placement === 'bottom') {
                arrowTop  = -aH / 2;
                arrowLeft = (tr.left + tr.width / 2) - menuLeft - aW / 2;
            } else if (placement === 'top') {
                arrowTop  = mH - aH / 2;
                arrowLeft = (tr.left + tr.width / 2) - menuLeft - aW / 2;
            } else if (placement === 'right') {
                arrowTop  = (tr.top + tr.height / 2) - menuTop - aH / 2;
                arrowLeft = -aW / 2;
            } else if (placement === 'left') {
                arrowTop  = (tr.top + tr.height / 2) - menuTop - aH / 2;
                arrowLeft = mW - aW / 2;
            }

            arrowLeft = Math.max(8, Math.min(arrowLeft, mW - aW - 8));
            arrowTop  = Math.max(8, Math.min(arrowTop,  mH - aH - 8));

            arrow.style.position = 'absolute';
            arrow.style.top      = `${arrowTop}px`;
            arrow.style.left     = `${arrowLeft}px`;
            arrow.setAttribute('data-placement', placement);
        },


        _on(el, type, fn) {
            el.addEventListener(type, fn);
            this._cleanup.push({ el, type, fn });
        },

        _watch() {
            const self = this;
            self.$watch('open', (val) => {
                self._targetEl?.setAttribute('aria-expanded', val ? 'true' : 'false');
                if (val) self.$root.setAttribute('open', '');
                else self.$root.removeAttribute('open');
                self.$root.dispatchEvent(new CustomEvent(val ? 'open' : 'close', { bubbles: true }));

                if (val) {
                    if (!self._reposition) {
                        self._reposition = () => { if (self.open) self._position(); };
                        window.addEventListener('resize', self._reposition, { passive: true });
                        window.addEventListener('scroll', self._reposition, { passive: true, capture: true });
                    }
                } else {
                    if (self._reposition) {
                        window.removeEventListener('resize', self._reposition);
                        window.removeEventListener('scroll', self._reposition, true);
                        self._reposition = null;
                    }
                }
            });
        },

    }));

}

/*

<button tooltip="Kaydet">save</button>
<button tooltip="Sil" placement="top">sil</button>
<button tooltip="Bilgi" placement="right" open-delay="300">info️</button>

ATTRİBUTELER — tooltip directive:
  tooltip       tooltip metni (zorunlu)
  placement     top|bottom|left|right + -center|-end  (default: top-center)
  offset        px                                     (default: 6)
  open-delay    ms                                     (default: 0)
  close-delay   ms                                     (default: 0)

CSS HOOK'LARI — tooltip:
  [data-tooltip]              tooltip elementi
  [data-tooltip][open]        görünür tooltip
  [data-tooltip][data-placement]  yön (top/bottom/left/right)

Örnek CSS:
  [data-tooltip] {
      padding: 4px 8px;
      background: #111;
      color: #fff;
      border-radius: 4px;
      font-size: 12px;
      pointer-events: none;
      opacity: 0;
      transition: opacity .15s;
  }
  [data-tooltip][open] { opacity: 1; }

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

KULLANIM — 1: Ayrı element (target attr)

<button id="myBtn">Popover Aç</button>

<div x-data="popover" target="#myBtn" trigger="click" placement="bottom-center">
  <div popover-arrow></div>
  <p>Popover içeriği</p>
  <button popover-dismiss>Kapat</button>
</div>


KULLANIM — 2: Wrapper içinde (previousElementSibling)

<div x-data="popover" trigger="hover" placement="top">
  <button slot="trigger">Hover et</button>  ← target otomatik previousElementSibling olur
  <div popover-arrow></div>
  <p>Popover içeriği</p>
</div>

Not: target attr yoksa popover'ın hemen önceki kardeş elementi hedef alınır.


ATTRİBUTELER — Wrapper:
  target          CSS selector — yoksa previousElementSibling
  trigger         click | hover              (default: click)
  placement       auto|top|bottom|left|right (default: auto)
                  + align: top-center, bottom-end, right-center vs.
  offset          px cinsinden boşluk        (default: 8)
  open-delay      ms — hover açılma gecikmesi
  close-delay     ms — hover kapanma gecikmesi
  focus-trap      Tab tuşunu popover içinde döngüye sokar
  open            sayfa açılınca açık gelsin

ATTRİBUTELER — İçerik:
  popover-arrow    ok elementi — JS pozisyonlar
  popover-dismiss  tıklayınca popover'ı kapatır

CSS HOOK'LARI:
  [x-data="popover"][open]              açık popover
  [x-data="popover"][data-placement]    yerleşim yönü (top/bottom/left/right)
  [popover-arrow][data-placement]       ok yönü

EVENTLER:
  open   → popover açıldı
  close  → popover kapandı

JS API:
  el._x_dataStack[0].open()
  el._x_dataStack[0].close()
  el._x_dataStack[0].toggle()
*/